import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import {
  Ban,
  Check,
  Eye,
  Percent,
  RefreshCw,
  Search,
  Sparkles,
  Star,
  Trash2,
  Zap,
} from 'lucide-react';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import { formatMoney, currencySymbol } from '../../components/quote/TotalsPanel';
import './tools.css';

const TABS = [
  {
    key: 'custom',
    labelKey: 'tools.discounts.tabCustom',
    hintKey: 'tools.discounts.hintCustom',
    Icon: Star,
  },
  {
    key: 'excluded',
    labelKey: 'tools.discounts.tabExcluded',
    hintKey: 'tools.discounts.hintExcluded',
    Icon: Ban,
  },
  {
    key: 'preview',
    labelKey: 'tools.discounts.tabPreview',
    hintKey: 'tools.discounts.hintPreview',
    Icon: Eye,
  },
];

const SAMPLE_LIST = 1000;

const defaultSettings = {
  enabled: false,
  calculateFrom: 'product_price',
  globalDiscountPct: 0,
  applyToAdminQuotes: false,
  autoShippingRatePick: 'cheapest',
};

const AutomaticDiscountsPage = ({ storeCurrency = 'USD' }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('custom');
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null); // 'settings' | 'custom' | 'exclusions' | 'preview'
  const [settings, setSettings] = useState(defaultSettings);
  const [customPrices, setCustomPrices] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [preview, setPreview] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const searchTimerRef = useRef(null);
  const currency = storeCurrency || 'USD';

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, customRes, exclRes] = await Promise.all([
        api.getDiscountSettings(),
        api.getCustomPrices(),
        api.getDiscountExclusions(),
      ]);
      setSettings({ ...defaultSettings, ...(settingsRes?.settings || {}) });
      setCustomPrices(customRes?.customPrices || []);
      setExclusions(exclRes?.exclusions || []);
    } catch (err) {
      showToast(err.message || t('tools.discounts.toast.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const runSearch = async (rawTerm) => {
    const term = String(rawTerm ?? searchQuery).trim();
    if (!term) {
      setSearchResults([]);
      setSearchAttempted(false);
      return;
    }
    setSearching(true);
    setSearchAttempted(true);
    try {
      const data = await api.searchProducts(term);
      setSearchResults(data?.products || []);
    } catch (err) {
      setSearchResults([]);
      showToast(err.message || t('tools.discounts.toast.searchFailed'), 'error');
    } finally {
      setSearching(false);
    }
  };

  const onSearchQueryChange = (value) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const term = value.trim();
    if (term.length < 1) {
      setSearchResults([]);
      setSearchAttempted(false);
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      runSearch(term);
    }, 300);
  };

  const clearSearch = () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setSearchQuery('');
    setSearchResults([]);
    setSearchAttempted(false);
  };

  const saveSettings = async () => {
    setSavingKey('settings');
    try {
      const data = await api.putDiscountSettings({
        enabled: Boolean(settings.enabled),
        calculateFrom: settings.calculateFrom,
        globalDiscountPct: Number(settings.globalDiscountPct) || 0,
        applyToAdminQuotes: Boolean(settings.applyToAdminQuotes),
        autoShippingRatePick:
          settings.autoShippingRatePick === 'highest' ? 'highest' : 'cheapest',
      });
      setSettings({ ...defaultSettings, ...(data?.settings || {}) });
      showToast(t('tools.discounts.toast.settingsSaved'), 'success');
    } catch (err) {
      showToast(err.message || t('tools.discounts.toast.saveSettingsFailed'), 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const saveCustomPrices = async () => {
    setSavingKey('custom');
    try {
      const data = await api.putCustomPrices(customPrices);
      setCustomPrices(data?.customPrices || []);
      showToast(t('tools.discounts.toast.customSaved'), 'success');
    } catch (err) {
      showToast(err.message || t('tools.discounts.toast.saveCustomFailed'), 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const saveExclusions = async () => {
    setSavingKey('exclusions');
    try {
      const data = await api.putDiscountExclusions(exclusions);
      setExclusions(data?.exclusions || []);
      showToast(t('tools.discounts.toast.exclusionsSaved'), 'success');
    } catch (err) {
      showToast(err.message || t('tools.discounts.toast.saveExclusionsFailed'), 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const loadPreview = async () => {
    setSavingKey('preview');
    try {
      const data = await api.previewDiscounts();
      setPreview(data?.preview || []);
      if (data?.settings) {
        setSettings({ ...defaultSettings, ...data.settings });
      }
    } catch (err) {
      showToast(err.message || t('tools.discounts.toast.previewFailed'), 'error');
    } finally {
      setSavingKey(null);
    }
  };

  useEffect(() => {
    if (tab === 'preview') loadPreview();
    else clearSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const addCustomFromProduct = (product) => {
    if (customPrices.some((p) => p.productId === String(product.productId))) {
      showToast(t('tools.discounts.toast.alreadyCustom'), 'warning');
      return;
    }
    const price = Number(product.price) || 0;
    const storePrice = Number(product.storePrice ?? product.price) || 0;
    const pct = Number(settings.globalDiscountPct) || 0;
    const customPrice = roundDown(price * (1 - pct / 100));
    setCustomPrices((prev) => [
      ...prev,
      {
        productId: String(product.productId),
        sku: product.sku,
        name: product.name,
        productPrice: price,
        storePrice,
        customPrice,
      },
    ]);
    clearSearch();
  };

  const updateCustomPrice = (productId, value) => {
    setCustomPrices((prev) =>
      prev.map((p) =>
        p.productId === productId ? { ...p, customPrice: parseFloat(value) || 0 } : p,
      ),
    );
  };

  const removeCustom = (productId) => {
    setCustomPrices((prev) => prev.filter((p) => p.productId !== productId));
  };

  const addExclusion = (product) => {
    if (exclusions.some((e) => e.productId === String(product.productId))) {
      showToast(t('tools.discounts.toast.alreadyExcluded'), 'warning');
      return;
    }
    setExclusions((prev) => [
      ...prev,
      {
        productId: String(product.productId),
        sku: product.sku,
        name: product.name,
      },
    ]);
    clearSearch();
  };

  const removeExclusion = (productId) => {
    setExclusions((prev) => prev.filter((e) => e.productId !== productId));
  };

  const pct = Number(settings.globalDiscountPct) || 0;
  const sampleAfter = useMemo(() => roundDown(SAMPLE_LIST * (1 - pct / 100)), [pct]);
  const activeTab = TABS.find((tb) => tb.key === tab) || TABS[0];
  const baseLabel =
    settings.calculateFrom === 'store_price'
      ? t('tools.discounts.storePrice')
      : t('tools.discounts.productPrice');

  const previewStats = useMemo(() => {
    const rows = preview || [];
    return {
      total: rows.length,
      discounted: rows.filter((r) => Number(r.discountAmount) > 0).length,
      custom: rows.filter((r) => r.rule === 'custom_price').length,
      excluded: rows.filter((r) => r.rule === 'excluded').length,
    };
  }, [preview]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="q2c-tools-page q2c-disc-page">
      <section className={`q2c-disc-hero${settings.enabled ? ' is-on' : ''}`}>
        <div className="q2c-disc-hero-copy">
          <div className="q2c-disc-hero-kicker">
            <Sparkles size={14} />
            {t('tools.discounts.heroKicker')}
          </div>
          <h2 className="q2c-disc-hero-title">{t('tools.discounts.heroTitle')}</h2>
          <p className="q2c-disc-hero-sub">
            {t('tools.discounts.heroSub')}
          </p>
          <div className="q2c-disc-hero-pills">
            <span className={`q2c-disc-pill${settings.enabled ? ' is-live' : ''}`}>
              <Zap size={13} />
              {settings.enabled ? t('tools.discounts.liveOnQuotes') : t('tools.discounts.paused')}
            </span>
            <span className="q2c-disc-pill">
              <Percent size={13} />
              {t('tools.discounts.offBase', { pct, base: baseLabel.toLowerCase() })}
            </span>
            <span className="q2c-disc-pill">
              <Star size={13} />
              {t('tools.discounts.customCount', { count: customPrices.length })}
            </span>
            <span className="q2c-disc-pill">
              <Ban size={13} />
              {t('tools.discounts.excludedCount', { count: exclusions.length })}
            </span>
          </div>
        </div>

        <div className="q2c-disc-dial" aria-hidden="true">
          <div
            className="q2c-disc-dial-ring"
            style={{
              background: `conic-gradient(#38bdf8 0deg ${pct * 3.6}deg, rgba(255,255,255,0.12) ${pct * 3.6}deg 360deg)`,
            }}
          >
            <div className="q2c-disc-dial-core">
              <div className="q2c-disc-dial-pct">{pct}%</div>
              <div className="q2c-disc-dial-label">{t('tools.discounts.globalOff')}</div>
            </div>
          </div>
          <div className="q2c-disc-sample">
            <span className="q2c-disc-sample-was">{formatMoney(SAMPLE_LIST, currency)}</span>
            <span className="q2c-disc-sample-arrow">→</span>
            <span className="q2c-disc-sample-now">{formatMoney(sampleAfter, currency)}</span>
          </div>
        </div>
      </section>

      <section className="q2c-disc-panel q2c-disc-rate">
        <header className="q2c-disc-panel-head">
          <span className="q2c-disc-step">1</span>
          <div>
            <h3>{t('tools.discounts.step1Title')}</h3>
            <p>{t('tools.discounts.step1Sub')}</p>
          </div>
        </header>

        <div className="q2c-disc-rate-grid">
          <button
            type="button"
            className={`q2c-disc-power${settings.enabled ? ' is-on' : ''}`}
            onClick={() => setSettings((s) => ({ ...s, enabled: !s.enabled }))}
            aria-pressed={Boolean(settings.enabled)}
          >
            <span className="q2c-disc-power-dot" />
            <span className="q2c-disc-power-copy">
              <strong>{settings.enabled ? t('tools.discounts.discountsOn') : t('tools.discounts.discountsOff')}</strong>
              <small>
                {settings.enabled
                  ? t('tools.discounts.onSub')
                  : t('tools.discounts.offSub')}
              </small>
            </span>
          </button>

          <div className="q2c-disc-base">
            <div className="q2c-disc-field-label">{t('tools.discounts.calculateFrom')}</div>
            <div className="q2c-disc-seg" role="radiogroup" aria-label={t('tools.discounts.priceBase')}>
              <button
                type="button"
                role="radio"
                aria-checked={settings.calculateFrom !== 'store_price'}
                className={settings.calculateFrom !== 'store_price' ? 'is-active' : ''}
                onClick={() => setSettings((s) => ({ ...s, calculateFrom: 'product_price' }))}
              >
                {t('tools.discounts.productPrice')}
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={settings.calculateFrom === 'store_price'}
                className={settings.calculateFrom === 'store_price' ? 'is-active' : ''}
                onClick={() => setSettings((s) => ({ ...s, calculateFrom: 'store_price' }))}
              >
                {t('tools.discounts.storePrice')}
              </button>
            </div>
            <p className="q2c-disc-hint">{t('tools.discounts.baseHint')}</p>
          </div>

          <div className="q2c-disc-slider-block">
            <div className="q2c-disc-slider-top">
              <div className="q2c-disc-field-label">{t('tools.discounts.discountPercentage')}</div>
              <span className="q2c-disc-pct-badge">{pct}%</span>
            </div>
            <Form.Range
              min={0}
              max={100}
              step={1}
              value={pct}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  globalDiscountPct: parseInt(e.target.value, 10) || 0,
                }))
              }
            />
            <div className="q2c-disc-slider-marks">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
            <div className="q2c-disc-quick">
              {[5, 10, 15, 20, 25].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={pct === n ? 'is-active' : ''}
                  onClick={() => setSettings((s) => ({ ...s, globalDiscountPct: n }))}
                >
                  {n}%
                </button>
              ))}
            </div>
          </div>

          <div className="q2c-disc-base">
            <div className="q2c-disc-field-label">{t('tools.discounts.shippingRatePick')}</div>
            <Form.Select
              value={settings.autoShippingRatePick === 'highest' ? 'highest' : 'cheapest'}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  autoShippingRatePick: e.target.value === 'highest' ? 'highest' : 'cheapest',
                }))
              }
            >
              <option value="cheapest">{t('tools.discounts.cheapestRate')}</option>
              <option value="highest">{t('tools.discounts.highestRate')}</option>
            </Form.Select>
            <p className="q2c-disc-hint">{t('tools.discounts.shippingRatePickHelp')}</p>
          </div>
        </div>

        <div className="q2c-disc-actions">
          <Button
            type="button"
            variant="primary"
            className="q2c-tools-save-btn"
            onClick={saveSettings}
            disabled={savingKey === 'settings'}
          >
            <Check size={16} />
            {savingKey === 'settings' ? t('tools.discounts.saving') : t('tools.discounts.saveGlobalRate')}
          </Button>
        </div>
      </section>

      <section className="q2c-disc-panel q2c-disc-workspace">
        <header className="q2c-disc-panel-head">
          <span className="q2c-disc-step">2</span>
          <div>
            <h3>{t('tools.discounts.step2Title')}</h3>
            <p>{t(activeTab.hintKey)}</p>
          </div>
        </header>

        <div className="q2c-disc-tabs" role="tablist" aria-label={t('tools.discounts.exceptionsAria')}>
          {TABS.map(({ key, labelKey, Icon }) => {
            const count =
              key === 'custom'
                ? customPrices.length
                : key === 'excluded'
                  ? exclusions.length
                  : previewStats.total || null;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`q2c-disc-tab${tab === key ? ' is-active' : ''}`}
                onClick={() => setTab(key)}
              >
                <Icon size={15} />
                <span>{t(labelKey)}</span>
                {count != null && count > 0 ? (
                  <em className="q2c-disc-tab-count">{count}</em>
                ) : null}
              </button>
            );
          })}
        </div>

        {tab === 'custom' && (
          <div className="q2c-disc-pane">
            <ProductSearchBlock
              searchQuery={searchQuery}
              searching={searching}
              searchAttempted={searchAttempted}
              searchResults={searchResults}
              onSearchQueryChange={onSearchQueryChange}
              onRunSearch={runSearch}
              onPick={addCustomFromProduct}
              pickLabel={t('tools.discounts.setCustom')}
              showPrice
              currency={currency}
              t={t}
            />

            {customPrices.length === 0 ? (
              <EmptyState
                icon={Star}
                title={t('tools.discounts.noCustomTitle')}
                body={t('tools.discounts.noCustomBody')}
              />
            ) : (
              <div className="q2c-disc-list">
                {customPrices.map((row) => (
                  <div key={row.productId} className="q2c-disc-row q2c-disc-row--custom">
                    <div className="q2c-disc-row-main">
                      <div className="q2c-disc-row-name">{row.name}</div>
                      <div className="q2c-disc-row-meta">
                        <span className="font-monospace">{row.sku || '—'}</span>
                        <span>{t('tools.discounts.listLabel', { price: formatMoney(row.productPrice, currency) })}</span>
                        <span>{t('tools.discounts.storeLabelValue', { price: formatMoney(row.storePrice, currency) })}</span>
                      </div>
                    </div>
                    <div className="q2c-disc-row-price">
                      <label htmlFor={`cp-${row.productId}`}>{t('tools.discounts.customLabel')}</label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text py-0 px-2">{currencySymbol(currency)}</span>
                        <Form.Control
                          id={`cp-${row.productId}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.customPrice}
                          onChange={(e) => updateCustomPrice(row.productId, e.target.value)}
                        />
                      </div>
                    </div>
                    <Button
                      variant="link"
                      className="q2c-disc-row-remove"
                      onClick={() => removeCustom(row.productId)}
                      aria-label={t('tools.discounts.removeAria', { name: row.name })}
                    >
                      <Trash2 size={15} />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="q2c-disc-actions">
              <Button
                type="button"
                variant="primary"
                className="q2c-tools-save-btn"
                onClick={saveCustomPrices}
                disabled={savingKey === 'custom'}
              >
                <Check size={16} />
                {savingKey === 'custom' ? t('tools.discounts.saving') : t('tools.discounts.saveCustomPrices')}
              </Button>
            </div>
          </div>
        )}

        {tab === 'excluded' && (
          <div className="q2c-disc-pane">
            <ProductSearchBlock
              searchQuery={searchQuery}
              searching={searching}
              searchAttempted={searchAttempted}
              searchResults={searchResults}
              onSearchQueryChange={onSearchQueryChange}
              onRunSearch={runSearch}
              onPick={addExclusion}
              pickLabel={t('tools.discounts.exclude')}
              showPrice={false}
              currency={currency}
              t={t}
            />

            {exclusions.length === 0 ? (
              <EmptyState
                icon={Ban}
                title={t('tools.discounts.nothingExcludedTitle')}
                body={t('tools.discounts.nothingExcludedBody')}
              />
            ) : (
              <div className="q2c-disc-list">
                {exclusions.map((row) => (
                  <div key={row.productId} className="q2c-disc-row q2c-disc-row--excluded">
                    <div className="q2c-disc-row-icon">
                      <Ban size={16} />
                    </div>
                    <div className="q2c-disc-row-main">
                      <div className="q2c-disc-row-name">{row.name || '—'}</div>
                      <div className="q2c-disc-row-meta">
                        <span className="font-monospace">{row.sku || '—'}</span>
                        <span className="font-monospace">{t('tools.discounts.idLabel', { id: row.productId })}</span>
                      </div>
                    </div>
                    <Button
                      variant="link"
                      className="q2c-disc-row-remove"
                      onClick={() => removeExclusion(row.productId)}
                      aria-label={t('tools.discounts.removeExclusionAria', { name: row.name })}
                    >
                      <Trash2 size={15} />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="q2c-disc-actions">
              <Button
                type="button"
                variant="primary"
                className="q2c-tools-save-btn"
                onClick={saveExclusions}
                disabled={savingKey === 'exclusions'}
              >
                <Check size={16} />
                {savingKey === 'exclusions' ? t('tools.discounts.saving') : t('tools.discounts.saveExclusions')}
              </Button>
            </div>
          </div>
        )}

        {tab === 'preview' && (
          <div className="q2c-disc-pane">
            <div className="q2c-disc-preview-bar">
              <div className="q2c-disc-preview-stats">
                <div>
                  <span>{t('tools.discounts.previewStatus')}</span>
                  <strong>{settings.enabled ? t('tools.discounts.enabled') : t('tools.discounts.disabled')}</strong>
                </div>
                <div>
                  <span>{t('tools.discounts.previewGlobal')}</span>
                  <strong>{pct}%</strong>
                </div>
                <div>
                  <span>{t('tools.discounts.previewBase')}</span>
                  <strong>{baseLabel}</strong>
                </div>
                <div>
                  <span>{t('tools.discounts.discounted')}</span>
                  <strong>
                    {previewStats.discounted}/{previewStats.total || 0}
                  </strong>
                </div>
              </div>
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                className="q2c-disc-refresh"
                onClick={loadPreview}
                disabled={savingKey === 'preview'}
              >
                <RefreshCw size={14} />
                {t('tools.discounts.refresh')}
              </Button>
            </div>

            {preview.length === 0 ? (
              <EmptyState
                icon={Eye}
                title={t('tools.discounts.noPreviewTitle')}
                body={t('tools.discounts.noPreviewBody')}
              />
            ) : (
              <div className="table-responsive q2c-disc-preview-table">
                <Table className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>{t('tools.discounts.colProduct')}</th>
                      <th>{t('tools.discounts.colList')}</th>
                      <th>{t('tools.discounts.colStore')}</th>
                      <th>{t('tools.discounts.colAfter')}</th>
                      <th>{t('tools.discounts.colDiscount')}</th>
                      <th>{t('tools.discounts.colRule')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row) => (
                      <tr key={row.productId}>
                        <td>
                          <div className="q2c-disc-preview-name">{row.name}</div>
                          <div className="font-monospace small text-muted">{row.sku || '—'}</div>
                        </td>
                        <td>{formatMoney(row.productPrice, currency)}</td>
                        <td>{formatMoney(row.storePrice, currency)}</td>
                        <td className="fw-semibold">{formatMoney(row.unitPrice, currency)}</td>
                        <td>
                          {row.discountAmount > 0 ? (
                            <span className="q2c-disc-save">
                              −{formatMoney(row.discountAmount, currency)}
                              <em>{row.discountPct}%</em>
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <span className={`q2c-disc-rule q2c-disc-rule--${row.rule || 'none'}`}>
                            {ruleLabel(row.rule, t)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

function ProductSearchBlock({
  searchQuery,
  searching,
  searchAttempted,
  searchResults,
  onSearchQueryChange,
  onRunSearch,
  onPick,
  pickLabel,
  showPrice,
  currency = 'USD',
  t,
}) {
  return (
    <div className="q2c-disc-search">
      <label className="q2c-disc-field-label" htmlFor="disc-product-search">
        {t('tools.discounts.findProduct')}
      </label>
      <div className="q2c-tools-search-row">
        <Form.Control
          id="disc-product-search"
          placeholder={t('tools.discounts.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onRunSearch())}
        />
        <Button type="button" variant="primary" onClick={() => onRunSearch()} disabled={searching}>
          <Search size={14} className="me-1" />
          {searching ? t('tools.discounts.searching') : t('tools.discounts.search')}
        </Button>
      </div>

      {searchAttempted && searchResults.length === 0 && !searching ? (
        <div className="q2c-disc-search-empty">{t('tools.discounts.noProductsFound', { query: searchQuery.trim() })}</div>
      ) : null}

      {searchResults.length > 0 && (
        <div className="q2c-disc-search-results">
          {searchResults.map((p) => (
            <div key={p.productId} className="q2c-disc-search-hit">
              <div>
                <div className="q2c-disc-row-name">{p.name}</div>
                <div className="q2c-disc-row-meta">
                  <span className="font-monospace">{p.sku || '—'}</span>
                  {showPrice ? <span>{formatMoney(p.price, currency)}</span> : null}
                </div>
              </div>
              <Button size="sm" variant="outline-primary" onClick={() => onPick(p)}>
                {pickLabel}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="q2c-disc-empty">
      <div className="q2c-disc-empty-icon">
        <Icon size={22} />
      </div>
      <h4>{title}</h4>
      <p>{body}</p>
    </div>
  );
}

function ruleLabel(rule, t) {
  if (rule === 'custom_price') return t('tools.discounts.ruleCustom');
  if (rule === 'global_pct') return t('tools.discounts.ruleGlobal');
  if (rule === 'excluded') return t('tools.discounts.ruleExcluded');
  return t('tools.discounts.ruleNone');
}

function roundDown(n) {
  return Math.round(n * 100) / 100;
}

export default AutomaticDiscountsPage;
